import { usePlaybackPosition, WorkbenchContext } from '@/contexts/WorkbenchContext';
import type {
  LoaderCallbacks,
  LoaderConfiguration,
  LoaderContext,
  LoaderResponse,
  LoaderStats,
} from 'hls.js';
import Hls from 'hls.js';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  /** Callback when video element is ready */
  onReady?(player: HTMLVideoElement): void;
  /** Height of the video player */
  height?: number;
  /** Video source URL */
  src?: string;
  /** Show native HTML5 controls (default: false for custom controls) */
  showNativeControls?: boolean;
  /** Clip start time in seconds (clamps playback to this point) */
  startSec?: number;
  /** Clip end time in seconds (clamps playback to this point) */
  endSec?: number;
}

let isModifiedInLastMinute = false;

/**
 * Loader that strips `#EXT-X-ENDLIST` so Hls.js keeps treating the stream as live.
 */
class EndlistStrippingLoader extends Hls.DefaultConfig.loader {
  load(
    context: LoaderContext,
    config: LoaderConfiguration,
    callbacks: LoaderCallbacks<LoaderContext>,
  ): void {
    const { onSuccess } = callbacks;

    const wrappedCallbacks: LoaderCallbacks<LoaderContext> = {
      ...callbacks,
      onSuccess: (
        response: LoaderResponse,
        stats: LoaderStats,
        loaderContext: LoaderContext,
        networkDetails?: XMLHttpRequest,
      ) => {
        if (loaderContext.responseType === 'text' && typeof response.data === 'string') {
          const lastModified = networkDetails
            ? networkDetails.getResponseHeader('last-modified')
            : '';
          isModifiedInLastMinute = lastModified
            ? new Date(lastModified).getTime() > Date.now() - 60000
            : true;
          // Strip #EXT-X-ENDLIST to force hls.js to treat as live stream
          let data = response.data;
          data = data.replace(/#EXT-X-ENDLIST\s*/g, '');

          if (process.env.NODE_ENV === 'development') {
            data = data.replace(
              /https:\/\/eos-projects-test\.s3-accelerate\.amazonaws\.com/g,
              '/dev-media-proxy',
            );
          }
          response.data = data;
        }

        onSuccess(response, stats, loaderContext, networkDetails);
      },
    };

    super.load(context, config, wrappedCallbacks);
  }
}

/**
 * Simple HLS video player component
 * Supports both native HLS (Safari) and hls.js for other browsers
 * When startSec/endSec are provided, clamps playback to that range and shows clip-relative duration
 */
const SimpleHlsPlayer: FC<Props> = ({
  onReady,
  src,
  showNativeControls = false,
  startSec = 0,
  endSec,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  // Track in-flight play() Promise to avoid "interrupted by pause()" errors
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const { setCurrentPosition, currentPosition } = usePlaybackPosition();
  const { isVideoPaused, setIsVideoPaused, isVideoMuted } = useContext(WorkbenchContext);

  // Clip-relative state for clamped playback
  const isClipMode = startSec > 0 || endSec != null;
  const [segDuration, setSegDuration] = useState<number>(
    Math.max(0, (endSec ?? 0) - (startSec || 0)),
  );
  const [currentSegTime, setCurrentSegTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  // Setup HLS source
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let playableSrc = src;
    if (
      process.env.NODE_ENV === 'development' &&
      src.includes('eos-projects-test.s3-accelerate.amazonaws.com')
    ) {
      playableSrc = src.replace(
        'https://eos-projects-test.s3-accelerate.amazonaws.com',
        '/dev-media-proxy',
      );
    }

    const isHlsUrl =
      /\.m3u8($|\?)/i.test(playableSrc) ||
      playableSrc.includes('format=m3u8') ||
      playableSrc.includes('application/vnd.apple.mpegurl');

    const setupNative = (): void => {
      video.src = playableSrc;
    };

    if (isHlsUrl) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          loader: EndlistStrippingLoader,
          // Force live stream behavior even if playlist has EXT-X-ENDLIST
        });
        hls.loadSource(playableSrc);
        hls.attachMedia(video);

        hls.on(Hls.Events.ERROR, function (event, data) {
          console.warn('[SimpleHlsPlayer] HLS error:', data.type, data.details, data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error(
                  '[SimpleHlsPlayer] Fatal network error - check URL, CORS, and dev-media-proxy',
                );
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('[SimpleHlsPlayer] Fatal media error, attempting recovery');
                hls.recoverMediaError();
                break;
              default:
                console.error('[SimpleHlsPlayer] Fatal HLS error, destroying instance');
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else {
        // Fallback: try setting src directly
        setupNative();
      }
    } else {
      setupNative();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute('src');
      try {
        video.load();
      } catch (e) {
        // ignore
      }
    };
  }, [src]);

  // Enforce clamped playback range when startSec/endSec are set
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isClipMode) return;

    const clamp = (t: number): number => {
      const s = Math.max(0, startSec || 0);
      const e = endSec != null && endSec > 0 ? endSec : Number.POSITIVE_INFINITY;
      if (t < s) return s;
      if (t > e) return e;
      return t;
    };

    const handleLoadedMetadata = (): void => {
      // Seek to the start of the segment
      try {
        video.currentTime = clamp(startSec || 0);
      } catch (_) {
        // some browsers require a play() before precise seek; ignore
      }
      const computedEnd =
        endSec != null ? endSec : isFinite(video.duration) ? video.duration : undefined;
      if (computedEnd != null) {
        setSegDuration(Math.max(0, computedEnd - (startSec || 0)));
      }
      setCurrentSegTime(0);
    };

    const handleSeeking = (): void => {
      const desired = clamp(video.currentTime);
      if (Math.abs(desired - video.currentTime) > 0.01) {
        video.currentTime = desired;
      }
    };

    const handleTimeUpdate = (): void => {
      if (endSec != null && video.currentTime >= endSec) {
        // Halt playback exactly at end boundary
        video.currentTime = endSec;
        video.pause();
        setIsPlaying(false);
      }
      // Prevent drifting before start
      if (video.currentTime < (startSec || 0)) {
        video.currentTime = startSec || 0;
      }
      const within = Math.max(0, video.currentTime - (startSec || 0));
      setCurrentSegTime(within);
    };

    const handlePlay = (): void => setIsPlaying(true);
    const handlePause = (): void => setIsPlaying(false);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // If already loaded, seek immediately
    if (video.readyState >= 1) {
      handleLoadedMetadata();
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [isClipMode, startSec, endSec]);

  // Pass video element to parent immediately so controls can render (don't wait for load)
  useEffect(() => {
    const video = videoRef.current;
    if (video && onReady) {
      onReady(video);
    }
  }, [onReady]);

  // Notify parent when video is ready and ensure proper initialization (seek position)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hasCalledReady = false;

    const callReadyOnce = (): void => {
      if (!hasCalledReady && onReady) {
        hasCalledReady = true;
        // Use currentPosition if it's been set by user interaction, otherwise use isModifiedInLastMinute logic
        if (currentPosition && currentPosition.source !== 'video-initial-load') {
          video.currentTime = currentPosition.timeSec;
        } else if (isModifiedInLastMinute) {
          video.currentTime = video.duration - 60; // Seek to near live point if modified recently
        } else {
          video.currentTime = 0;
        }
        onReady(video);
      }
    };

    video.addEventListener('loadedmetadata', callReadyOnce);
    video.addEventListener('canplay', callReadyOnce);

    // Also call immediately if video is already ready
    if (video.readyState >= video.HAVE_ENOUGH_DATA) {
      callReadyOnce();
    }

    return () => {
      video.removeEventListener('loadedmetadata', callReadyOnce);
      video.removeEventListener('canplay', callReadyOnce);
    };
    // currentPosition is intentionally omitted to avoid multiple calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReady]);

  // Sync video time updates with context (with seeking protection)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = (): void => {
      const currentTime = video.currentTime;
      const isPaused = video.paused || video.ended;
      setCurrentPosition(currentTime, isPaused ? 'video-scrubbing' : 'video-playing');
    };

    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [setCurrentPosition]);

  useEffect(() => {
    if (videoRef.current && currentPosition && !currentPosition.source.startsWith('video-')) {
      // Only seek if the source is not video-playing or video-scrubbing to avoid loops
      videoRef.current.currentTime = currentPosition.timeSec;
    }
  }, [currentPosition]);

  // Sync pause/play state to context
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePause = (): void => {
      setIsVideoPaused(true);
    };

    const handlePlay = (): void => {
      setIsVideoPaused(false);
    };

    video.addEventListener('pause', handlePause);
    video.addEventListener('play', handlePlay);
    video.addEventListener('ended', handlePause);

    return () => {
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('ended', handlePause);
    };
  }, [setIsVideoPaused]);

  const handleError = (): void => {
    const video = videoRef.current;
    if (video?.error) {
      console.error('[SimpleHlsPlayer] Video error:', video.error.code, video.error.message);
    }
  };

  const handleVideoClick = (): void => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      // Store the Promise so a rapid second click can wait for it before pausing
      playPromiseRef.current = video.play();
      playPromiseRef.current
        .catch((err: Error) => {
          // AbortError is expected when pause() interrupts a pending play() — ignore it
          if (err.name !== 'AbortError') {
            console.error('[SimpleHlsPlayer] Play error:', err);
          }
        })
        .finally(() => {
          playPromiseRef.current = null;
        });
    } else if (playPromiseRef.current) {
      // play() hasn't resolved yet — pause as soon as it does to avoid the race
      playPromiseRef.current.then(() => video.pause()).catch(() => undefined);
    } else {
      video.pause();
    }
  };

  // Format time as M:SS for clip controls
  const fmt = useCallback((t: number): string => {
    if (!isFinite(t)) return '0:00';
    const sign = t < 0 ? '-' : '';
    const abs = Math.max(0, Math.floor(Math.abs(t)));
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${sign}${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  const durationLabel = useMemo(() => fmt(segDuration || 0), [fmt, segDuration]);
  const currentLabel = useMemo(() => fmt(currentSegTime || 0), [fmt, currentSegTime]);

  // Toggle play/pause for clip mode
  const togglePlay = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      const s = startSec || 0;
      const e = endSec ?? Number.POSITIVE_INFINITY;
      if (video.currentTime >= e - 0.01) {
        video.currentTime = s;
      }
      void video.play();
      setIsPlaying(true);
    }
  }, [isPlaying, startSec, endSec]);

  // Scrub handler for clip controls
  const onScrub = useCallback(
    (value: number): void => {
      const video = videoRef.current;
      if (!video) return;
      const s = startSec || 0;
      const duration = segDuration || 0;
      const v = Math.max(0, Math.min(duration, value));
      video.currentTime = s + v;
      setCurrentSegTime(v);
    },
    [startSec, segDuration],
  );

  // For clip mode, show custom controls with clip-relative time
  if (isClipMode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
        <video
          ref={videoRef}
          autoPlay={false}
          muted={isVideoMuted}
          controls={false}
          playsInline
          preload="auto"
          disablePictureInPicture
          onError={handleError}
          onClick={togglePlay}
          style={{
            width: '100%',
            flex: 1,
            display: 'block',
            backgroundColor: '#000',
            objectFit: 'contain',
            cursor: 'pointer',
          }}
        />
        {/* Custom controls showing clip-relative duration */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 0',
            width: '100%',
          }}
        >
          <button type="button" onClick={togglePlay} style={{ padding: '4px 12px' }}>
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 40 }}>{currentLabel}</span>
          <input
            type="range"
            min={0}
            max={Math.max(0.01, segDuration || 0)}
            step={0.1}
            value={Math.min(currentSegTime, segDuration || 0)}
            onChange={(e) => onScrub(parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
          <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 40 }}>{durationLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay={!isVideoPaused}
      muted={isVideoMuted}
      controls={showNativeControls}
      playsInline
      preload="auto"
      disablePictureInPicture
      onError={handleError}
      onClick={handleVideoClick}
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        backgroundColor: '#000',
        objectFit: 'contain',
        cursor: 'pointer',
      }}
    />
  );
};

export default SimpleHlsPlayer;
